/**
 * 时间工具：展示统一使用 Asia/Shanghai（UTC+8，无夏令时），
 * 传输给 API 的始终是 UTC ISO 字符串（15 号文档第 9 节）。
 */

const SHANGHAI_OFFSET_MS = 8 * 60 * 60 * 1000;

const pad = (value: number): string => String(value).padStart(2, '0');

/** ISO（UTC）→ Asia/Shanghai 墙钟时间，格式 YYYY-MM-DD HH:mm */
export function formatShanghai(iso: string | null | undefined): string {
  if (!iso) {
    return '-';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }
  const shifted = new Date(date.getTime() + SHANGHAI_OFFSET_MS);
  return (
    `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}-${pad(shifted.getUTCDate())}` +
    ` ${pad(shifted.getUTCHours())}:${pad(shifted.getUTCMinutes())}`
  );
}

/** ISO（UTC）→ Asia/Shanghai 日期，格式 YYYY-MM-DD */
export function formatShanghaiDate(iso: string | null | undefined): string {
  const formatted = formatShanghai(iso);
  return formatted === '-' ? '-' : formatted.slice(0, 10);
}

/** ISO（UTC）→ 表单可编辑的 Asia/Shanghai 字符串（YYYY-MM-DD HH:mm） */
export function toShanghaiInput(iso: string | null | undefined): string {
  const formatted = formatShanghai(iso);
  return formatted === '-' ? '' : formatted;
}

/** 拆分为 [日期, 时间]，供表格两行日期单元格（DateTimeCell）使用 */
export function shanghaiDateTimeParts(iso: string | null | undefined): [string, string] | null {
  const formatted = formatShanghai(iso);
  if (formatted === '-') {
    return null;
  }
  const [date, time] = formatted.split(' ');
  return [date, time ?? ''];
}

/** 当前时刻的 Asia/Shanghai 表单字符串 */
export function nowShanghaiInput(): string {
  return formatShanghai(new Date().toISOString());
}

const SHANGHAI_INPUT_PATTERN = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/;

export interface ParseResult {
  ok: boolean;
  iso?: string;
  message?: string;
}

/**
 * 解析 Asia/Shanghai 墙钟时间字符串（YYYY-MM-DD 或 YYYY-MM-DD HH:mm[:ss]），
 * 返回 UTC ISO 字符串。
 */
export function parseShanghaiInput(value: string): ParseResult {
  const trimmed = value.trim();
  if (!trimmed) {
    return { ok: false, message: '时间不能为空' };
  }
  const matched = SHANGHAI_INPUT_PATTERN.exec(trimmed);
  if (!matched) {
    return { ok: false, message: '格式应为 YYYY-MM-DD HH:mm（Asia/Shanghai）' };
  }
  const [, year, month, day, hour, minute, second] = matched;
  const parts = [year, month, day, hour ?? '00', minute ?? '00', second ?? '00'].map((item) =>
    Number(item),
  );
  const [y, mo, d, h, mi, s] = parts as [number, number, number, number, number, number];
  if (mo < 1 || mo > 12 || d < 1 || d > 31 || h > 23 || mi > 59 || s > 59) {
    return { ok: false, message: '时间数值不合法' };
  }
  const utcMs = Date.UTC(y, mo - 1, d, h, mi, s) - SHANGHAI_OFFSET_MS;
  const date = new Date(utcMs);
  if (Number.isNaN(date.getTime())) {
    return { ok: false, message: '时间不合法' };
  }
  // 校验反向换算一致，拦截 2026-02-31 之类的假日期
  const shifted = new Date(date.getTime() + SHANGHAI_OFFSET_MS);
  if (
    shifted.getUTCFullYear() !== y ||
    shifted.getUTCMonth() + 1 !== mo ||
    shifted.getUTCDate() !== d
  ) {
    return { ok: false, message: '日期不存在' };
  }
  return { ok: true, iso: date.toISOString() };
}

/** 是否已生效（用于首页选品/定时发布的本地提示，最终以服务端为准） */
export function isEffective(
  startsAt: string | null | undefined,
  endsAt: string | null | undefined,
  now: number = Date.now(),
): boolean {
  if (startsAt) {
    const start = new Date(startsAt).getTime();
    if (!Number.isNaN(start) && start > now) {
      return false;
    }
  }
  if (endsAt) {
    const end = new Date(endsAt).getTime();
    if (!Number.isNaN(end) && end <= now) {
      return false;
    }
  }
  return true;
}
